import {
  FilterMenuPill,
  FilterMultiMenuPill,
} from "@/components/molecules/filter-pills";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useForms } from "@/features/qualifications/hooks/use-qualification-forms";
import type { QualificationStatus } from "@/features/qualifications/types";
import { useTranslation } from "@/features/utils/i18n";
import { SortDescendingIcon } from "@phosphor-icons/react";
import { startOfDay, subDays } from "date-fns";

export type SubmissionFilters = {
  /** Several at once; `undefined` is every status. */
  status?: QualificationStatus[];
  form_id?: string;
  score?: "high" | "medium" | "low";
  completion?: "complete" | "incomplete";
  review?: "reviewed" | "unreviewed";
  period?: "today" | "7d" | "30d" | "90d";
};

/**
 * What the list opens on: the rejected submissions are out of the way — they are
 * decided and nobody gets back to them. One click on the status pill brings them.
 */
export const DEFAULT_FILTERS: SubmissionFilters = {
  status: ["pending", "accepted"],
};

export type SubmissionSort = "score" | "recent" | "oldest" | "score_asc";

/** The same three bands as the score's colours: good, to look at, poor. */
const SCORE_BANDS = {
  high: { score_min: 0.8 },
  medium: { score_min: 0.5, score_max: 0.8 },
  low: { score_max: 0.5 },
} as const;

const PERIOD_DAYS = { today: 0, "7d": 7, "30d": 30, "90d": 90 } as const;

/** The filters and the sort as the search route reads them. */
export const toQuery = (filters: SubmissionFilters, sort: SubmissionSort) => ({
  status: filters.status?.length ? filters.status.join(",") : undefined,
  form_id: filters.form_id,
  ...(filters.score ? SCORE_BANDS[filters.score] : {}),
  completed: filters.completion ? filters.completion === "complete" : undefined,
  reviewed: filters.review ? filters.review === "reviewed" : undefined,
  // From midnight, so the query (and its cache key) holds still all day.
  date_from: filters.period
    ? startOfDay(subDays(new Date(), PERIOD_DAYS[filters.period])).toISOString()
    : undefined,
  sort_by:
    sort === "score" || sort === "score_asc"
      ? "score,created_at"
      : "created_at",
  sort_order: (sort === "oldest" || sort === "score_asc" ? "asc" : "desc") as
    | "asc"
    | "desc",
});

export const activeFilterCount = (filters: SubmissionFilters) =>
  Object.values(filters).filter((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value),
  ).length;

/**
 * Every way the list can be cut, as the call history's pills: off, a pill reads
 * as what it filters; set, it is filled, reads as its value and has a cross.
 */
export const QualificationsFilters = ({
  filters,
  onFiltersChange,
}: {
  filters: SubmissionFilters;
  onFiltersChange: (filters: SubmissionFilters) => void;
}) => {
  const { t } = useTranslation("qualifications");
  const { data: forms = [] } = useForms();

  const pill = <K extends Exclude<keyof SubmissionFilters, "status">>(
    key: K,
    options: { value: NonNullable<SubmissionFilters[K]>; label: string }[],
  ) => (
    <FilterMenuPill
      key={key}
      label={t(`submissions.filters.${key}`)}
      clearLabel={t("submissions.filters.clear", {
        name: t(`submissions.filters.${key}`),
      })}
      value={filters[key] as string | undefined}
      onChange={(value) =>
        onFiltersChange({
          ...filters,
          [key]: value as SubmissionFilters[K] | undefined,
        })
      }
      allLabel={t(`submissions.filters.all.${key}`)}
      options={options}
    />
  );

  return (
    <>
      <FilterMultiMenuPill
        label={t("submissions.filters.status")}
        clearLabel={t("submissions.filters.clear", {
          name: t("submissions.filters.status"),
        })}
        values={filters.status}
        onChange={(status) => onFiltersChange({ ...filters, status })}
        allLabel={t("submissions.filters.all.status")}
        options={(["pending", "accepted", "rejected"] as const).map(
          (value) => ({ value, label: t(`submissions.statuses.${value}`) }),
        )}
      />
      {forms.length > 1 &&
        pill(
          "form_id",
          forms.map((form) => ({ value: form.id, label: form.name })),
        )}
      {pill(
        "score",
        (["high", "medium", "low"] as const).map((value) => ({
          value,
          label: t(`submissions.filters.scores.${value}`),
        })),
      )}
      {pill(
        "period",
        (["today", "7d", "30d", "90d"] as const).map((value) => ({
          value,
          label: t(`submissions.filters.periods.${value}`),
        })),
      )}
      {pill(
        "completion",
        (["complete", "incomplete"] as const).map((value) => ({
          value,
          label: t(`submissions.completion.${value}`),
        })),
      )}
      {pill(
        "review",
        (["reviewed", "unreviewed"] as const).map((value) => ({
          value,
          label: t(`submissions.filters.reviews.${value}`),
        })),
      )}
    </>
  );
};

/** Best first by default: the list is a queue of people to get back to. */
export const SortMenu = ({
  value,
  onChange,
}: {
  value: SubmissionSort;
  onChange: (value: SubmissionSort) => void;
}) => {
  const { t } = useTranslation("qualifications");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground"
        >
          <SortDescendingIcon className="size-3.5" />
          {t(`submissions.sort.${value}`)}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(v) => onChange(v as SubmissionSort)}
        >
          {(["score", "recent", "oldest", "score_asc"] as const).map((sort) => (
            <DropdownMenuRadioItem key={sort} value={sort}>
              {t(`submissions.sort.${sort}`)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
